import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';

import {
    ApiError,
    TradingService,
    AuthApiService,
    CompanyExtendedModel,
    IndividualExtendedModel,
    TradingAccountDetailedReportModel,
    CashAccountDetailedReportModel
} from '@spa/api';

import {FormComponent} from '../../../../shared/widgets/form';
import {FormInterface, PermissionRuleInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components/dialog';

import {ChangeTradingAccountStatusForm} from '../../forms';
import {ChangeTradingAccountStatusFormErrorStatesEnum} from '../../enums/error-states';

import {ChangeTradingAccountStatusMessages} from '../../../../../assets/messages';

@Component({
    selector: 'app-change-trading-account-statuses',
    templateUrl: './change-trading-account-status.component.html',
    styleUrls: ['./change-trading-account-status.component.scss']
})
export class ChangeTradingAccountStatusComponent
    extends DialogComponent<ChangeTradingAccountStatusComponent, boolean> implements OnInit, OnDestroy, FormInterface {

    private changeAccountStatusSubscription: Subscription;

    public form: ChangeTradingAccountStatusForm;
    public hasExactChangeStatusError: boolean;

    constructor(
        private authApiService: AuthApiService,
        private tradingService: TradingService,
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
            client: IndividualExtendedModel | CompanyExtendedModel
            triggers: number[],
            restrictUpdateStatusToClosedPermissionRule: PermissionRuleInterface
        },
        public dialogRef: MatDialogRef<ChangeTradingAccountStatusComponent>
    ) {
        super(dialogRef);

        this.config = data.config;
        this.config.process.succeedMessage = ChangeTradingAccountStatusMessages.UpdateAccountStatusSucceeded.message;

        this.hasExactChangeStatusError = false;
    }

    ngOnInit(): void {
        this.initForm();
    }

    ngOnDestroy(): void {
        if (this.changeAccountStatusSubscription) {
            this.changeAccountStatusSubscription.unsubscribe();
        }
    }

    getFormMessages(controlLabel?: string, controlMaxLength?: number): { [p: string]: string } {
        const differentStatusValidationKey = ChangeTradingAccountStatusMessages.DifferentStatusValidationFailed.key;

        const differentStatusValidationMessage =
            ChangeTradingAccountStatusMessages.DifferentStatusValidationFailed.message;

        return {
            ...FormComponent.getFormMessages(controlLabel, controlMaxLength),
            [differentStatusValidationKey]: differentStatusValidationMessage
        };
    }

    initForm(): void {
        this.form = new ChangeTradingAccountStatusForm(this.data.client, this.data.account, this.tradingService);

        this.form.initForm(this.data.triggers);
    }

    validateForm(): boolean {
        return this.form.validateForm(
            this.getFormMessages,
            ChangeTradingAccountStatusMessages.DifferentStatusValidationFailed.key
        );
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close();
        } else {
            if (this.validateForm()) {
                const authUserPermissions = !this.authApiService.getAuthenticatedUser() ? [] :
                    this.authApiService.getAuthenticatedUser().permissions;

                this.startLoading(() => {
                    this.changeAccountStatusSubscription = this.form
                        .createRequest(this.data.restrictUpdateStatusToClosedPermissionRule.permissions, authUserPermissions)
                        .subscribe((response: ApiError | boolean) => {
                            const notActiveOwnerErrorCode =
                                ChangeTradingAccountStatusFormErrorStatesEnum.NotActiveAccountOwner;

                            const changeToClosedRestrictedErrorCode =
                                ChangeTradingAccountStatusFormErrorStatesEnum.UpdateTradingAccountStatusToClosedRestricted;

                            if (!(response instanceof ApiError)) {
                                this.config.process.finished = true;
                            } else {
                                switch (response.status) {
                                    case notActiveOwnerErrorCode:
                                        this.config.process.failedMessage =
                                            ChangeTradingAccountStatusMessages.NotActiveAccountOwner.message;

                                        this.hasExactChangeStatusError = true;
                                        break;
                                    case changeToClosedRestrictedErrorCode:
                                        this.config.process.failedMessage =
                                            ChangeTradingAccountStatusMessages.ChangeStatusToClosedRestricted.message;

                                        this.hasExactChangeStatusError = true;
                                        break;
                                    default:
                                        this.config.process.failedMessage =
                                            ChangeTradingAccountStatusMessages.UnknownError.message;

                                        this.config.process.failed = true;
                                        this.config.process.finished = true;
                                }
                            }

                            this.config.process.loading = false;
                        });
                });
            }
        }
    }
}
